/**
 * HrV1LaunchEpr.tsx
 *
 * Dedicated full-page launcher for Step 1 (EPR submission). Replaces
 * the inline launch form previously on HrV1Console — gives the work
 * room to breathe and adopts the legacy "Start a Review" wizard tone.
 *
 * Route: /hr-v1/:cycleId/launch/epr (HR-only via RoleRoute)
 *
 * 3-step flow:
 *   1. Set up      — confirm cycle + scoring template
 *   2. Employees   — pick employees, supervisors, level, OKR Dept
 *   3. Send        — review summary, click Send → success view with
 *                    distribution links
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { routes } from '../routes';
import { useToast } from '../components/ui/Toast';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import LaunchWizard, { type LaunchWizardStep } from '../components/epr/LaunchWizard';
import EmployeeBuilder from '../components/epr/EmployeeBuilder';
import DistributionPackage from '../components/epr/DistributionPackage';
import type { DistributionLink } from '../components/epr/DistributionPackage';
import { fetchDirectory } from '../services/directoryService';
import type { DirectoryEmployee } from '../types/magicLink';
import type { ReviewCycle } from '../types';
import { launchStep1, type LaunchStep1Input } from '../services/hrCycleLauncher';
import { sendMagicLinkEmailsBulk } from '../services/emailService';

const HrV1LaunchEpr: React.FC = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const toast = useToast();

  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [templateId, setTemplateId] = useState('default-hr-v1');
  const [employees, setEmployees] = useState<LaunchStep1Input['employees']>([]);
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [links, setLinks] = useState<DistributionLink[]>([]);
  // Per-token send result after the bulk auto-send fires. Keyed by
  // tokenId so DistributionPackage rows render "✓ Sent" / "✗ Failed"
  // immediately on the success tab without needing HR to re-click Send.
  const [initialSendStatus, setInitialSendStatus] = useState<Record<string, 'sent' | 'error'>>({});

  // ── Load cycle + directory eagerly. Both are needed for the wizard
  //    to operate (cycle for context, directory for the employee picker).
  useEffect(() => {
    if (!cycleId) return;
    let alive = true;
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'reviewCycles', cycleId));
        if (!alive) return;
        if (snap.exists()) {
          setCycle({ id: snap.id, ...(snap.data() as Omit<ReviewCycle, 'id'>) });
          setTemplateId((snap.data() as { hrFrameworkTemplateId?: string }).hrFrameworkTemplateId ?? 'default-hr-v1');
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cycleId]);

  useEffect(() => {
    if (directory.length > 0) return;
    let alive = true;
    void fetchDirectory().then(r => { if (alive) setDirectory(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [directory.length]);

  // ── Submit
  //
  // Two phases — both run on a single "Send N links" click after the
  // user confirms the modal:
  //
  //   Phase 1: launchStep1 — creates eprSubmissions docs + issues the
  //            magicLinkTokens. Pure Firestore writes, no email yet.
  //
  //   Phase 2: sendMagicLinkEmailsBulk — invokes the sendMagicLinkEmail
  //            Cloud Function once per token. Resend HTTP API behind it.
  //            Per-token errors don't abort the batch.
  //
  // This used to stop at Phase 1, which was the bug HR reported:
  // "I clicked Send but recipients never got the email." Phase 1
  // generated tokens and showed them in a Distribution panel — HR was
  // then expected to click *another* per-row Send. Easy miss. We now
  // do both atomically and surface the per-token send status in the
  // success view.
  const doLaunch = useCallback(async () => {
    if (!cycle) return;
    setSubmitting(true);
    try {
      // Phase 1: create submissions + tokens.
      const res = await launchStep1({
        cycleId: cycle.id,
        templateId: templateId.trim() || 'default-hr-v1',
        employees,
        // Canonical form header — Review Period auto-derived from the
        // cycle so nobody types it on the form (EPR Form.docx header).
        reviewPeriod: {
          label: `${cycle.period} ${cycle.year}`,
          from:  toIsoDate(cycle.startDate),
          to:    toIsoDate(cycle.endDate),
        },
      });

      const next: DistributionLink[] = [];
      for (let i = 0; i < employees.length; i++) {
        const emp = employees[i];
        const selfTok = res.selfTokens[i];
        const mgrTok  = res.managerTokens[i];
        next.push({
          recipientEmail: emp.employeeEmail,
          recipientName:  emp.employeeName,
          action:         'EPR self-assessment',
          url:            selfTok?.url ?? '',
          tokenId:        selfTok?.token,
        });
        next.push({
          recipientEmail: emp.supervisorEmail,
          recipientName:  emp.supervisorName,
          action:         'EPR manager review',
          context:        `reviewing ${emp.employeeName}`,
          url:            mgrTok?.url ?? '',
          tokenId:        mgrTok?.token,
        });
      }
      setLinks(next);
      toast.show(`Created ${res.submissions.length} submissions — sending emails...`, { icon: 'check' });

      // Phase 2: bulk-send the magic-link emails. Per-token errors are
      // captured in the result array, so a single failure doesn't block
      // the rest from going out.
      const tokenIds = next.map(l => l.tokenId).filter((t): t is string => Boolean(t));
      const sendResults = await sendMagicLinkEmailsBulk(tokenIds);

      const status: Record<string, 'sent' | 'error'> = {};
      let sentCount = 0;
      let failedCount = 0;
      for (const r of sendResults) {
        status[r.tokenId] = r.ok ? 'sent' : 'error';
        if (r.ok) sentCount += 1;
        else      failedCount += 1;
      }
      setInitialSendStatus(status);

      if (failedCount === 0) {
        toast.show(`All ${sentCount} emails sent.`, { icon: 'check' });
      } else if (sentCount === 0) {
        toast.show(`Tokens created but ${failedCount} emails failed. See the rows for details.`, { icon: 'error' });
      } else {
        toast.show(`Sent ${sentCount} — ${failedCount} failed. Click Resend on the failed rows.`, { icon: 'warning' });
      }
    } catch (e) {
      toast.show(`Launch failed: ${(e as Error).message}`, { icon: 'error' });
      throw e;
    } finally {
      setSubmitting(false);
    }
  }, [cycle, templateId, employees, toast]);

  // ── Build the wizard step list. Memo'd so isValid closures don't
  //    re-evaluate on every render that didn't change state.
  const steps: LaunchWizardStep[] = useMemo(() => {
    if (!cycle) return [];
    return [
      {
        id: 'setup',
        label: 'Set up',
        isValid: () => true,
        content: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="REVIEW CYCLE">
                <div className="rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm">
                  {cycle.name}
                </div>
              </Field>
              <Field label="PERIOD">
                <div className="rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm tabular-nums">
                  {cycle.period} {cycle.year}
                </div>
              </Field>
            </div>
            <Field label="SCORING TEMPLATE">
              <select
                value={templateId}
                onChange={e => setTemplateId(e.target.value)}
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white"
              >
                <option value="default-hr-v1">
                  default-hr-v1 — OKR 30% · KPI 40% · Core 20% · Leadership 10%
                </option>
              </select>
              <div className="text-xs text-stone-500 mt-1">
                Pinned to the canonical hr_v1 rubric. Custom per-division
                templates are on the roadmap.
              </div>
            </Field>
          </div>
        ),
      },
      {
        id: 'employees',
        label: 'Employees',
        isValid: () => employees.length > 0,
        invalidHint: 'Add at least one employee before sending the links.',
        content: (
          <div className="space-y-3">
            <div className="text-sm text-stone-700">
              Employees · <b className="tabular-nums">{employees.length}</b> added
              {directory.length > 0 && (
                <span className="ml-2 text-xs font-normal text-stone-500">
                  · {directory.length} in directory
                </span>
              )}
            </div>
            <EmployeeBuilder
              value={employees}
              onChange={setEmployees}
              directory={directory}
              cycleYear={cycle.year}
              cyclePeriod={cycle.period}
            />
          </div>
        ),
      },
      {
        id: 'send',
        label: 'Send',
        isValid: () => employees.length > 0,
        content: (
          <div className="space-y-4">
            <div className="text-sm text-stone-700">
              Ready to send <b>{employees.length * 2}</b> magic-link
              {employees.length * 2 !== 1 ? 's' : ''} for
              <b> {employees.length}</b> employee{employees.length !== 1 ? 's' : ''}.
            </div>
            <ul className="rounded-lg border border-stone-200 bg-stone-50 divide-y divide-stone-200 text-sm">
              {employees.map((e, i) => (
                <li key={`${e.employeeId}-${i}`} className="px-3 py-2 flex justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-stone-900 truncate">
                      {e.employeeName || '(unnamed)'}
                    </div>
                    <div className="text-xs text-stone-500 truncate">
                      {e.employeeEmail}
                      {e.position ? ` · ${e.position}` : ''}
                      {' · '}supervisor {e.supervisorName}
                      {' · '}{e.okrItems.length} OKR · {e.kpiItems.length} KPI
                    </div>
                  </div>
                  <span className="text-xs text-stone-500 flex-shrink-0">2 links</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-stone-500">
              Sending will create EPR submission documents and issue magic-link
              tokens. Default expiry is 15 days, single-use. Idempotent — existing
              submissions are not overwritten.
            </p>
          </div>
        ),
      },
    ];
  }, [cycle, templateId, employees, directory]);

  const successView = links.length > 0
    ? (
      <div className="space-y-4">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900">
          <div className="font-semibold mb-0.5">Links issued</div>
          {links.length} magic-link{links.length !== 1 ? 's' : ''} generated.
          Distribute to each recipient — they click the link and fill the form
          (no login needed).
        </div>
        <DistributionPackage
          title="Step 1 links"
          cycleName={cycle?.name}
          links={links}
          initialSendStatus={initialSendStatus}
        />
        <div className="flex justify-end">
          <Link to={cycle ? routes.hrV1Cycle(cycle.id) : routes.cyclesList()}>
            <Button variant="primary" icon="arrow_back">Back to cycle workflow</Button>
          </Link>
        </div>
      </div>
    )
    : null;

  // ── Terminal states (hooks above must always run first)
  if (loading) return <CenterMessage tone="info" title="Loading cycle..." />;
  if (error || !cycle || !cycleId) {
    return (
      <CenterMessage
        tone="error"
        title="Cannot open launcher"
        body={error ?? 'Cycle not found or removed.'}
        action={
          <Link to={routes.cyclesList()}>
            <Button variant="submit" icon="arrow_back">Back to Cycles</Button>
          </Link>
        }
      />
    );
  }
  if (cycle.frameworkVersion !== 'hr_v1') {
    return (
      <CenterMessage
        tone="warning"
        title="Not an hr_v1 cycle"
        body="This launcher only handles hr_v1 cycles. Open the legacy OPTICS launcher for other cycles."
      />
    );
  }

  const linkCount = employees.length * 2;

  return (
    <LaunchWizard
      eyebrow="HR WORKFLOW"
      title="Launch EPR Step 1"
      intro="Pick employees to review and we'll generate a unique magic link for each — one for the self-assessment, one for their manager. No login needed — they just click and fill."
      backHref={routes.hrV1Cycle(cycle.id)}
      backLabel="Back to cycle"
      steps={steps}
      onSubmit={doLaunch}
      submitLabel={`Send ${linkCount} emails`}
      submitting={submitting}
      successView={successView}
      confirmBeforeSubmit={{
        title: `Send ${linkCount} magic-link email${linkCount === 1 ? '' : 's'}?`,
        body: `This will create ${employees.length} EPR submission${employees.length === 1 ? '' : 's'} and email a unique link to each recipient. Recipients can fill the form without logging in.`,
        actions: [
          {
            label: `Create ${employees.length} submission document${employees.length === 1 ? '' : 's'}`,
            detail: 'Stored in Firestore. Idempotent — existing submissions are skipped.',
          },
          {
            label: `Issue ${linkCount} magic-link token${linkCount === 1 ? '' : 's'} (15-day expiry)`,
            detail: 'Single-use. Self-assessment + manager review per employee.',
          },
          {
            label: `Send ${linkCount} email${linkCount === 1 ? '' : 's'} via Resend`,
            detail: 'From epr@rascalrepublic.com. Per-row Resend button if any fail.',
          },
        ],
        confirmLabel: `Send ${linkCount} email${linkCount === 1 ? '' : 's'}`,
      }}
    />
  );
};

// ─── Helpers ───────────────────────────────────────────────────────

/** Firestore Timestamp → yyyy-mm-dd, or undefined when the field is absent. */
function toIsoDate(ts?: { toDate?: () => Date }): string | undefined {
  const d = ts?.toDate?.();
  return d ? d.toISOString().slice(0, 10) : undefined;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">{label}</div>
    {children}
  </label>
);

export default HrV1LaunchEpr;
